import {CommonErrorModal} from "@app/components/modals/CommonErrorModal"
import {CommonSelectModal, CommonModal} from "@app/components/modals/CommonModal"
import {GroupUpdateModal, GroupDeleteModal} from "@app/components/modals/GroupModal"
import {Switch, useModal, Button} from "@shortsworks/ui"
import {StoryGroupData, useDisableStoryGroup, useGetStoryGroupList} from "@shortsworks/user-api";
import {useQueryClient} from "@tanstack/react-query"
import {useReactTable, getCoreRowModel, flexRender, ColumnDef, Row} from "@tanstack/react-table"
import {ShareIcon, CopyIcon, EditIcon, TrashIcon} from "lucide-react"

// renderer 로 변경
const columns: ColumnDef<StoryGroupData>[] = [
    {
        header: '그룹 이름',
        accessorKey: 'name',
        size: 1000
    },
    {
        header: '활성화',
        accessorKey: 'isActive',
        size: 10,
        cell: ({row}: {row: Row<StoryGroupData>}) => {
            const group = row.original;
            const {showModal} = useModal();
            const queryClient = useQueryClient();
            const {queryKey: storyGroupListQueryKey} = useGetStoryGroupList();
            const { mutateAsync: mutateAsyncDisableStoryGroup } = useDisableStoryGroup({
                mutation: {
                    onSuccess: () => {
                        return queryClient.invalidateQueries({
                            queryKey: storyGroupListQueryKey
                        });
                    },
                }
            });

            return (
                <Switch
                    checked={!group.isDisabled}
                    onClick={async (event: React.MouseEvent) => {
                        event.preventDefault();
                        const title = group.isDisabled
                            ? "해당 그룹을 활성화하시겠습니까?"
                            : "해당 그룹을 비활성화하시겠습니까?";
                        const modalResult = await showModal(CommonSelectModal, {
                            title,
                        }).promise();
                        if (!modalResult.value) {
                            return;
                        }
                        const disableStoryGroupResult =
                            await mutateAsyncDisableStoryGroup({
                                groupId: group.id,
                                data: {
                                    disable: !group.isDisabled,
                                },
                            });
                        if (!disableStoryGroupResult.data.success) {
                            return showModal(CommonErrorModal, {
                                title: "변경을 실패했습니다.",
                            });
                        }
                    }}
                    className="min-w-fit"
                />
            );
        },
    },
    {
        header: '공유',
        size: 10,
        cell: ({row}: {row: Row<StoryGroupData>}) => {
            const group = row.original;
            const {showModal} = useModal();

            return (
                <Button
                    variant="outline"
                    onClick={async () => {
                        const sharedLinkURL =
                            import.meta.env.VITE_FONTEND_URL +
                            "/shared/" +
                            group.sharedLink?.id +
                            `?option=${btoa(JSON.stringify({ type: "group", width: "300px", "mobile-width": "200px" }))}` +
                            `&detail-view=true`;
                        await navigator.clipboard.writeText(sharedLinkURL);
                        return showModal(CommonModal, {
                            title: "공유 링크가 복사되었습니다.",
                        });
                    }}
                >
                    <CopyIcon className="h-2 w-2"/>
                </Button>
            )
        },
    },
    {
        header: '복사',
        size: 10,
        cell: ({row}: {row: Row<StoryGroupData>}) => {
            const group = row.original;
            const {showModal} = useModal();

            return (
                <Button
                    variant="outline"
                    onClick={async () => {
                        const text = `group-id=${group.id}`;
                        await navigator.clipboard.writeText(text);
                        return showModal(CommonModal, {
                            title: "그룹 ID가 복사되었습니다."
                        });
                    }}
                >
                    <CopyIcon className="h-2 w-2"/>
                </Button>
            )
        },
    },
    {
        header: '수정',
        size: 10,
        cell: ({row}: {row: Row<StoryGroupData>}) => {
            const group = row.original;
            const {showModal} = useModal();

            const handleUpdateGroup = ({ id, name }: StoryGroupData) => () => {
                return showModal(GroupUpdateModal, { id, name });
            };

            return (
                <Button
                    variant="outline"
                    onClick={handleUpdateGroup(group)}
                >
                    <EditIcon className="h-2 w-2"/>
                </Button>
            )
        },
    },
    {
        header: '삭제',
        size: 10,
        cell: ({row}: {row: Row<StoryGroupData>}) => {
            const group = row.original;
            const {showModal} = useModal();

            return (
                <Button
                    variant="outline"
                    onClick={() => showModal(GroupDeleteModal, { groupId: group.id })}
                >
                    <TrashIcon className="h-2 w-2"/>
                </Button>
            );
        }
    },
];

interface GroupTableProps {
    data: StoryGroupData[];
}
export const PostingGroupTable = ({data}: GroupTableProps) => {
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        defaultColumn: {
            size: 200,
            minSize: 100,
            // maxSize: 500,
        }
    });

    return (
        <table className="w-full">
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                        <th key={header.id}
                            className="text-left"
                            style={{ width: `${header.getSize()}px` }}
                        >
                            {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                )}
                        </th>
                    ))}
                </tr>
            ))}
            </thead>
            <tbody>
            {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                        <td className="py-2" key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    ))}
                </tr>
            ))}
            </tbody>
        </table>
    );
}